import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';

import { usePathname } from 'src/routes/hooks';
import { useResponsive } from 'src/hooks/use-responsive';

import Logo from 'src/components/logo';
import Scrollbar from 'src/components/scrollbar';
import { NavSectionVertical } from 'src/components/nav-section';

import { useWhitelabelStore } from 'src/store/whitelabel/whitelabel.store';
import Iconify from 'src/components/iconify';
import IconButton from '@mui/material/IconButton';
import { NAV } from '../config-layout';
import { useNavData } from './config-navigation';
import NavToggleButton from '../common/nav-toggle-button';

// ----------------------------------------------------------------------


type Props = {
  openNav: boolean;
  onCloseNav: VoidFunction;
};


export default function NavVertical({ openNav, onCloseNav }: Props) {
  const pathname = usePathname();
  const lgUp = useResponsive('up', 'lg');
  const navData = useNavData();
  const [allCollapsed, setAllCollapsed] = useState(false);
  const [collapsedDesktop] = useState(false);

  const handleToggleAllCollapse = () => {
    setAllCollapsed((prev) => !prev);
  };



  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="start" sx={{ mt: 2, ml: 4, mb: 1, mr: 2, height: 40 }}>
        <Logo
          width={!lgUp ? 56 : 50}
          height={!lgUp ? 24 : 15}
          customLogo={useWhitelabelStore.getState().whitelabel?.logo ?? '/logo/logo-nectar-laranja.png'}
          sx={{ minWidth: !lgUp ? 56 : 50, minHeight: !lgUp ? 24 : 15 }}
        />
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2, ml: 18, mb: 1, mr: 2, height: 40 }}>
          <IconButton
            onClick={handleToggleAllCollapse}
            sx={{ width: 24, height: 24, borderRadius: 5, border: '1px solid', borderColor: 'grey.300', p: 0 , mr: 2.5}}
          >
            <Iconify icon="eva:minus-outline" width={18} sx={{ color: 'grey.700' }} />
          </IconButton>
        </Stack>
      </Stack>
      <NavSectionVertical
        data={navData}
        slotProps={{
          allCollapsed,
          collapsed: collapsedDesktop && lgUp,
        }}
      />
      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ pb: { xs: 2, md: 4 } }} />
    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: collapsedDesktop ? 80 : NAV.W_VERTICAL },
        transition: 'width 0.2s',
      }}
    >
      <NavToggleButton />
      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: 'fixed',
            width: collapsedDesktop ? 80 : NAV.W_VERTICAL,
            borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
            transition: 'width 0.2s',
            backgroundColor: 'background.paper',
            zIndex: 1200,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.W_VERTICAL,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
